import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

import { Button } from "../components/Button.jsx";
import { UnifiedModal } from "../components/UnifiedModal.jsx";

export function TutorialIntroModal({
  open,
  onClose,
  onConfirm,
  isFirstTimePrompt = false,
  title,
  description,
  badge,
  highlightTitle,
  highlightDescription,
  bulletTitle = "Que vas a hacer",
  bullets = [],
  note,
  confirmLabel = "Empezar tutorial",
  cancelLabel = "Cancelar",
  firstTimeTitle = "Nuevo tutorial",
  firstTimeDescription = "Te damos la bienvenida. Hemos preparado un recorrido breve para enseñarte esta seccion y que empieces con contexto desde el primer momento.",
  firstTimeBadge = "Ejemplo guiado",
  firstTimeHighlightTitle = "Quieres que te guiemos en tu primera vez aqui?",
  firstTimeHighlightDescription = "En pocos pasos veras donde esta lo importante y como se usa esta herramienta dentro del flujo habitual de trabajo.",
  firstTimeBulletTitle = "Si lo empiezas ahora",
  firstTimeBullets = [
    "Verás las zonas clave de esta pagina sin tener que explorarlo todo a ciegas.",
    "Entenderás que puedes hacer aquí y cual es el flujo recomendado.",
  ],
  firstTimeNote = "Si lo omites, no volverá a aparecer automáticamente.",
  firstTimeConfirmLabel = "Empecemos",
  firstTimeCancelLabel = "Omitir tutorial",
}) {
  const resolvedTitle = isFirstTimePrompt ? firstTimeTitle : title;
  const resolvedDescription = isFirstTimePrompt ? firstTimeDescription : description;
  const resolvedBadge = isFirstTimePrompt ? firstTimeBadge : badge;
  const resolvedHighlightTitle = isFirstTimePrompt
    ? firstTimeHighlightTitle
    : highlightTitle;
  const resolvedHighlightDescription = isFirstTimePrompt
    ? firstTimeHighlightDescription
    : highlightDescription;
  const resolvedBulletTitle = isFirstTimePrompt
    ? firstTimeBulletTitle
    : bulletTitle;
  const resolvedBullets = isFirstTimePrompt ? firstTimeBullets : bullets;
  const resolvedNote = isFirstTimePrompt ? firstTimeNote : note;
  const resolvedConfirmLabel = isFirstTimePrompt
    ? firstTimeConfirmLabel
    : confirmLabel;
  const resolvedCancelLabel = isFirstTimePrompt
    ? firstTimeCancelLabel
    : cancelLabel;

  return (
    <UnifiedModal open={open} onClose={onClose} title={resolvedTitle} maxWidth="sm">
      <Box
        // Marcador común para que componentes globales detecten que hay un tutorial activo. (desde FloatingCopilotButton desactivarlo para evitar inconsistencias en UI)
        data-tutorial-active="true"
        data-tutorial-surface="intro"
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        {resolvedDescription && (
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
            {resolvedDescription}
          </Typography>
        )}

        {(resolvedBadge || resolvedHighlightTitle || resolvedHighlightDescription) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              p: 2,
              borderRadius: "12px",
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            {resolvedBadge && (
              <Box
                sx={{
                  alignSelf: "flex-start",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "999px",
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: "primary.contrastText",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {resolvedBadge}
              </Box>
            )}

            {resolvedHighlightTitle && (
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {resolvedHighlightTitle}
              </Typography>
            )}

            {resolvedHighlightDescription && (
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                {resolvedHighlightDescription}
              </Typography>
            )}
          </Box>
        )}

        {resolvedBullets.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {resolvedBulletTitle}
            </Typography>

            <Box component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
              {resolvedBullets.map((bullet) => (
                <Box
                  component="li"
                  key={bullet}
                  sx={{
                    mb: 1,
                    lineHeight: 1.7,
                    "&::marker": {
                      color: "text.primary",
                    },
                  }}
                >
                  <Typography variant="body2" component="span" sx={{ color: "text.secondary" }}>
                    {bullet}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {resolvedNote && (
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
            {resolvedNote}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          <Button variant="outline" onClick={() => onClose?.(false)}>
            {resolvedCancelLabel}
          </Button>
          <Button onClick={onConfirm}>
            {resolvedConfirmLabel}
          </Button>
        </Box>
      </Box>
    </UnifiedModal>
  );
}

TutorialIntroModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isFirstTimePrompt: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  badge: PropTypes.string,
  highlightTitle: PropTypes.string,
  highlightDescription: PropTypes.string,
  bulletTitle: PropTypes.string,
  bullets: PropTypes.arrayOf(PropTypes.string),
  note: PropTypes.node,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  firstTimeTitle: PropTypes.string,
  firstTimeDescription: PropTypes.string,
  firstTimeBadge: PropTypes.string,
  firstTimeHighlightTitle: PropTypes.string,
  firstTimeHighlightDescription: PropTypes.string,
  firstTimeBulletTitle: PropTypes.string,
  firstTimeBullets: PropTypes.arrayOf(PropTypes.string),
  firstTimeNote: PropTypes.node,
  firstTimeConfirmLabel: PropTypes.string,
  firstTimeCancelLabel: PropTypes.string,
};
